import { Icon } from "@mirage-web/icons";
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ensureSession } from "~/modules/auth/credentials";
import {
  uploadResumableFile,
  type ResumableUploadPhase,
} from "~/modules/file-upload/resumable-upload";
import type { CompleteUploadResponse } from "~/modules/file-upload/schemas";
import {
  extensionToMimeType,
  formatFileSize,
  mimeTypeToResumableAssetType,
} from "~/modules/file-upload/utils";

export const Route = createFileRoute("/_dash/file-upload")({
  component: RouteComponent,
  beforeLoad() {
    ensureSession();
  },
});

const UPLOAD_PHASE_LABELS: Record<ResumableUploadPhase, string> = {
  starting: "Starting upload session...",
  uploading: "Uploading to GCS...",
  completing: "Completing upload...",
};

function RouteComponent() {
  const [file, setFile] = useState<File | null>(null);
  const [bucket, setBucket] = useState("");
  const [prefix, setPrefix] = useState("");
  const [makePublic, setMakePublic] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<CompleteUploadResponse | null>(null);
  const [uploadPhase, setUploadPhase] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select a file");
      return;
    }

    if (!bucket.trim()) {
      toast.error("Bucket name is required");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setResult(null);
    abortControllerRef.current = new AbortController();

    const extension = file.name.split(".").pop() || "";
    const mimeType = file.type || extensionToMimeType(extension);
    const assetType = extension === "pag" ? "pag" : mimeTypeToResumableAssetType(mimeType);

    try {
      const completeResponse = await uploadResumableFile({
        file,
        assetType,
        mimeType,
        bucket: bucket.trim(),
        prefix: prefix.trim() || null,
        retention: "permanent",
        makePublic,
        onProgress: setProgress,
        onPhase: (phase) => setUploadPhase(UPLOAD_PHASE_LABELS[phase]),
        signal: abortControllerRef.current.signal,
      });

      setResult(completeResponse);
      toast.success("File uploaded successfully");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error instanceof Error && error.message === "Upload aborted") {
        toast.info("Upload cancelled");
      } else {
        toast.error("Upload failed", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      }
    } finally {
      setIsUploading(false);
      setProgress(0);
      setUploadPhase("");
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
  };

  return (
    <PermissionGuard permission="file-upload:view">
      <main className="p-6 space-y-6 max-w-2xl">
        <h1 className="text-2xl font-bold">File Upload</h1>

        <Card>
          <CardHeader>
            <CardTitle>Upload Configuration</CardTitle>
            <CardDescription>Configure file upload parameters</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">File *</Label>
                <Input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  required
                />
                {file && (
                  <p className="text-xs text-muted-foreground">
                    {file.name} ({formatFileSize(file.size)})
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bucket">Bucket *</Label>
                <Input
                  id="bucket"
                  placeholder="my-gcs-bucket"
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value)}
                  disabled={isUploading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prefix">Prefix (optional)</Label>
                <Input
                  id="prefix"
                  placeholder="folder/subfolder"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  disabled={isUploading}
                />
                <p className="text-xs text-muted-foreground">
                  Optional folder path prefix for the uploaded file
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="makePublic"
                  checked={makePublic}
                  onCheckedChange={(checked) => setMakePublic(checked === true)}
                  disabled={isUploading}
                />
                <Label htmlFor="makePublic" className="font-normal cursor-pointer">
                  Make file publicly accessible
                </Label>
              </div>

              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{uploadPhase}</span>
                    <span className="font-medium">{Math.round(progress * 100)}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" disabled={isUploading || !file} className="flex-1">
                  {isUploading ? "Uploading..." : "Upload File"}
                </Button>
                {isUploading && (
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <CardHeader>
              <CardTitle className="text-green-900 dark:text-green-100 flex items-center gap-2">
                <Icon name="check" className="size-5" />
                Upload Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="font-medium">Asset ID:</span>{" "}
                <code className="text-xs bg-green-100 dark:bg-green-900 px-1 py-0.5 rounded">
                  {result.asset_id}
                </code>
              </div>
              <div className="space-y-1">
                <span className="font-medium">Signed URL:</span>
                <div className="flex gap-2">
                  <Input
                    value={result.signed_url}
                    readOnly
                    className="text-xs bg-green-100 dark:bg-green-900"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(result.signed_url);
                      toast.success("URL copied to clipboard");
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </PermissionGuard>
  );
}
